import { Stack } from '@mui/material';
import {
  AvailableCriteria,
  FilterKeys,
  GetMiniListQueryFilters,
  GroupLimitationsUpdate,
  GroupUserType,
} from '@schooly/api';
import { SimpleListResult } from '@schooly/api';
import { MINI_LIST } from '@schooly/constants/test-ids';
import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { AddStaffExternalSidebar } from '../../../../components/common/AddStaffExternalSidebar/AddStaffExternalSidebar';
import { AddStaffFiltersKey } from '../../../../components/common/AddStaffExternalSidebar/AddStaffFiltersPopup';
import { AddStudentsExternalSidebar } from '../../../../components/common/AddStudentsExternalSidebar/AddStudentsExternalSidebar';
import { AddStudentsFiltersKey } from '../../../../components/common/AddStudentsExternalSidebar/AddStudentsFiltersPopup';
import { ModalPanel } from '../../../../components/uikit-components/Modal/Modal.styled';
import { ModalHeader } from '../../../../components/uikit-components/Modal/ModalHeader';
import { useLimitedTo } from '../../../../hooks/useLimitedTo';
import usePrevious from '../../../../hooks/usePrevious';
import GroupModalTabs from '../GroupModalTabs';
import { useAddGroupModal, ValidityRangeRequest } from '../useAddGroupModal';
import { GroupStudentsFilterPopup } from './GroupStudentsFilterPopup';
import AvailableCriteriaList from './AvailableCriteria';

import './index.scss';

const TABS = {
  criteria: 'groups-AvailableCriteria',
  users: 'groups-AvailableUsers',
};

const DEFAULT_FILTERS_AVAILABLE: AddStudentsFiltersKey[] = [
  FilterKeys.Date,
  FilterKeys.Status,
  FilterKeys.AgeGroup,
  FilterKeys.House,
  FilterKeys.Group,
  FilterKeys.Subject,
  FilterKeys.Gender,
  FilterKeys.Nationality,
];

const EMPTY_STAFF_FILTERS: AddStaffFiltersKey[] = [];
export interface ExternalSidebarProps {
  userType: GroupUserType;
  availableCriteria?: AvailableCriteria[];
  groupUsers: SimpleListResult[];
  groupCriteria: AvailableCriteria[];
  onCriteriaClick?: (criteria: AvailableCriteria) => void;
  onUserClick: (userId: string, user?: SimpleListResult) => void;
  onAddUsers: (users: SimpleListResult[]) => void;
  limitedTo: GroupLimitationsUpdate;
  validityRange: ValidityRangeRequest;
  titleId: string;
  deleteHistoryUsers?: SimpleListResult[];
  onRemovedUserClick?: (userId: string) => void;
  isFetching?: boolean;
}

const ExternalSidebar: React.FC<ExternalSidebarProps> = ({
  userType,
  availableCriteria,
  groupUsers,
  groupCriteria,
  onCriteriaClick,
  onUserClick,
  onAddUsers,
  limitedTo,
  validityRange,
  titleId,
  deleteHistoryUsers,
  onRemovedUserClick,
  isFetching,
}) => {
  const { formatMessage } = useIntl();
  const {
    activeTab,
    actions: { setActiveTab },
  } = useAddGroupModal({});

  const prevDeleteHistoryUsers = usePrevious(deleteHistoryUsers);
  const { filters: limitedToFilters } = useLimitedTo(userType, limitedTo);
  const handleStudentClick = (studentsList?: SimpleListResult | SimpleListResult[]) => {
    if (!studentsList) {
      return;
    }

    if (Array.isArray(studentsList)) {
      onAddUsers(studentsList);
      return;
    }

    if (deleteHistoryUsers?.some((user) => user.relation_id === studentsList.relation_id)) {
      onRemovedUserClick?.(studentsList.relation_id);
    }

    onUserClick(studentsList.relation_id, studentsList);
  };
  const handleStaffClick = (staffList?: SimpleListResult | SimpleListResult[]) => {
    if (!staffList) {
      return;
    }

    if (Array.isArray(staffList)) {
      onAddUsers(staffList);
      return;
    }

    if (deleteHistoryUsers?.some((user) => user.relation_id === staffList.relation_id)) {
      onRemovedUserClick?.(staffList.relation_id);
    }

    onUserClick(staffList.relation_id, staffList);
  };

  const tabs =
    userType === 'student' ? (
      <GroupModalTabs tabs={TABS} activeTab={activeTab} onTabSelect={setActiveTab} />
    ) : undefined;

  const initialFilters = useMemo(() => {
    return {
      ...limitedToFilters,
      [FilterKeys.Date]: validityRange,
    } as GetMiniListQueryFilters;
  }, [limitedToFilters, validityRange]);

  useEffect(() => {
    if (
      deleteHistoryUsers?.length &&
      prevDeleteHistoryUsers?.length !== deleteHistoryUsers.length &&
      activeTab !== 'users'
    ) {
      setActiveTab('users');
    }
  }, [deleteHistoryUsers?.length, activeTab, prevDeleteHistoryUsers?.length, setActiveTab]);

  if (!userType) {
    return null;
  }

  if (userType === 'staff') {
    return (
      <AddStaffExternalSidebar
        staff={groupUsers}
        onStaffClick={handleStaffClick}
        filtersAvailable={EMPTY_STAFF_FILTERS}
        initialFilters={initialFilters}
        deleteHistoryStaff={deleteHistoryUsers}
      />
    );
  }

  if (activeTab === 'users') {
    return (
      <AddStudentsExternalSidebar
        showExcludeGroupsToggle
        students={groupUsers}
        headerComponent={tabs}
        onStudentClick={handleStudentClick}
        deleteHistoryStudents={deleteHistoryUsers}
        filtersAvailable={DEFAULT_FILTERS_AVAILABLE}
        initialFilters={initialFilters}
        renderFiltersModal={(props) => <GroupStudentsFilterPopup {...props} />}
      />
    );
  }

  // TODO: drop className="GroupExternalSidebar"
  return (
    <Stack
      sx={{ height: '100%' }}
      className="GroupExternalSidebar"
      data-test-id={MINI_LIST.CONTAINER}
    >
      <ModalHeader title={formatMessage({ id: titleId })} active />
      <ModalPanel
        active
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        {tabs}
        <AvailableCriteriaList
          userType={userType}
          criteria={availableCriteria}
          groupCriteria={groupCriteria}
          onCriteriaClick={onCriteriaClick}
          isFetching={isFetching}
        />
      </ModalPanel>
    </Stack>
  );
};

export default ExternalSidebar;
